// Monotonic generation progress with soft-crawl between socket ticks.
//
// Server emits `progress` 0..1 (optional etaMsRemaining / crawlMs). Client never
// rolls back; between events it eases toward a soft ceiling so long AI jobs
// don't look frozen even without frequent emits.

import { useCallback, useEffect, useRef, useState } from "react";

function clamp01(v: number): number {
	if (Number.isNaN(v)) return 0;
	return Math.min(1, Math.max(0, v));
}

/** Default time to ease past a waypoint when server didn't send ETA. */
const DEFAULT_CRAWL_MS = 22_000;
/** How far past the last server floor we may crawl while waiting. */
const SOFT_AHEAD = 0.14;
/** Never soft-crawl into "done" without complete(). */
const SOFT_CAP = 0.97;
/** Tick interval for crawl animation. */
const DEFAULT_INTERVAL_MS = 200;

export interface GenerationProgressTick {
	/** Absolute progress floor 0..1 from server / stage. */
	progress: number;
	/** Remaining estimate for soft-crawl (ms). Preferred over crawlMs. */
	etaMsRemaining?: number;
	/** Explicit crawl duration to soft target when ETA unknown. */
	crawlMs?: number;
	/**
	 * Потолок soft-crawl для ЭТОГО тика (0..1). Например на стадии скрипта
	 * `0.35` — кольцо ползёт, но не притворяется, что TTS уже почти готов.
	 * Без поля — общий `SOFT_CAP` (0.97).
	 */
	softCap?: number;
}

export interface UseGenerationProgressOptions {
	/** Soft-crawl ceiling relative to gap to 1 when ETA is set (default 0.85). */
	crawlCap?: number;
	/** Tick interval for soft-crawl (ms). Default 200. */
	crawlIntervalMs?: number;
	/** Default crawl duration when neither eta nor crawlMs given. */
	defaultCrawlMs?: number;
}

export interface UseGenerationProgressResult {
	/** Display progress 0..1 (monotonic, animated). */
	progress: number;
	/** Rounded percent 0..100. */
	percent: number;
	/** Last ETA ms from server (raw), if any. */
	etaMsRemaining: number | null;
	/** Apply a server / stage tick. */
	applyTick: (tick: GenerationProgressTick) => void;
	/** Snap to 100% (ready). */
	complete: () => void;
	/** Reset to 0 (failed / unmount remount). */
	reset: () => void;
}

export function useGenerationProgress(
	options: UseGenerationProgressOptions = {},
): UseGenerationProgressResult {
	const crawlCap = options.crawlCap ?? 0.85;
	const crawlIntervalMs = options.crawlIntervalMs ?? DEFAULT_INTERVAL_MS;
	const defaultCrawlMs = options.defaultCrawlMs ?? DEFAULT_CRAWL_MS;

	const [progress, setProgress] = useState(0);
	const [etaMsRemaining, setEtaMs] = useState<number | null>(null);
	const [crawling, setCrawling] = useState(false);
	const progressRef = useRef(0);
	const serverFloorRef = useRef(0);
	const animFromRef = useRef(0);
	const crawlTargetRef = useRef(0);
	const crawlDurationRef = useRef(0);
	const crawlStartedAtRef = useRef<number | null>(null);
	const etaRef = useRef<number | null>(null);
	const softCapRef = useRef(SOFT_CAP);

	const startCrawl = useCallback((target: number, durationMs: number) => {
		const nextTarget = Math.max(crawlTargetRef.current, clamp01(target));
		animFromRef.current = progressRef.current;
		crawlTargetRef.current = nextTarget;
		crawlDurationRef.current = Math.max(250, durationMs);
		crawlStartedAtRef.current = Date.now();
		setCrawling(nextTarget > progressRef.current + 0.001);
	}, []);

	const applyTick = useCallback(
		(tick: GenerationProgressTick) => {
			const floor = clamp01(tick.progress);
			serverFloorRef.current = Math.max(serverFloorRef.current, floor);

			const eta =
				typeof tick.etaMsRemaining === "number" && tick.etaMsRemaining > 0
					? tick.etaMsRemaining
					: null;
			etaRef.current = eta;
			setEtaMs(eta);

			const stageCap =
				typeof tick.softCap === "number" && tick.softCap > 0 ? clamp01(tick.softCap) : SOFT_CAP;
			// Не max: смена стадии (script → TTS) должна поднять потолок, а не
			// оставлять старый SOFT_CAP от первого «голого» тика.
			softCapRef.current = stageCap;

			const explicitCrawlMs =
				typeof tick.crawlMs === "number" && tick.crawlMs > 0 ? tick.crawlMs : null;
			// crawlMs выигрывает у ETA как ДЛИТЕЛЬНОСТЬ анимации: вызывающий, который
			// шлёт оба поля, считает пол сам (по времени стадии на сервере) и просит
			// сгладить лишь промежуток до следующего тика — растягивать это сглаживание
			// на весь ETA значит снова рисовать «замёрзшее» кольцо. ETA при этом
			// остаётся для подписи «~N мин».
			const duration = explicitCrawlMs ?? eta ?? defaultCrawlMs;

			// Wide crawl (ETA или длинный crawlMs): тянем к crawlCap остатка до 1.
			// Короткий nudge: +SOFT_AHEAD за floor — для частых stage waypoints.
			const wideCrawl = eta != null || (explicitCrawlMs != null && explicitCrawlMs >= 8_000);
			const softTarget = wideCrawl
				? Math.min(stageCap, serverFloorRef.current + (1 - serverFloorRef.current) * crawlCap)
				: Math.min(stageCap, serverFloorRef.current + SOFT_AHEAD);

			// Snap floor into the animation baseline if display lagged behind server.
			if (progressRef.current < serverFloorRef.current) {
				const catchUp = Math.min(duration, 600);
				animFromRef.current = progressRef.current;
				crawlTargetRef.current = Math.max(softTarget, serverFloorRef.current);
				crawlDurationRef.current = Math.max(catchUp, duration);
				crawlStartedAtRef.current = Date.now();
				setCrawling(true);
				return;
			}

			startCrawl(softTarget, duration);
		},
		[crawlCap, defaultCrawlMs, startCrawl],
	);

	const complete = useCallback(() => {
		progressRef.current = 1;
		serverFloorRef.current = 1;
		animFromRef.current = 1;
		crawlTargetRef.current = 1;
		crawlDurationRef.current = 0;
		crawlStartedAtRef.current = null;
		etaRef.current = null;
		setEtaMs(null);
		setCrawling(false);
		setProgress(1);
	}, []);

	const reset = useCallback(() => {
		progressRef.current = 0;
		serverFloorRef.current = 0;
		animFromRef.current = 0;
		crawlTargetRef.current = 0;
		crawlDurationRef.current = 0;
		crawlStartedAtRef.current = null;
		etaRef.current = null;
		softCapRef.current = SOFT_CAP;
		setEtaMs(null);
		setCrawling(false);
		setProgress(0);
	}, []);

	useEffect(() => {
		if (!crawling) return;
		const id = window.setInterval(() => {
			const started = crawlStartedAtRef.current;
			const duration = crawlDurationRef.current;
			if (started == null || duration <= 0) {
				setCrawling(false);
				return;
			}
			const target = crawlTargetRef.current;
			if (progressRef.current >= target - 0.0005) {
				progressRef.current = Math.max(progressRef.current, target);
				setProgress(progressRef.current);
				// Хвост: до следующего server-тика медленно ползём к stage-cap,
				// чтобы длинный script не выглядел «замёрзшим на 19%».
				const cap = softCapRef.current;
				const gap = cap - progressRef.current;
				if (gap > 0.012 && serverFloorRef.current < 0.98) {
					animFromRef.current = progressRef.current;
					crawlTargetRef.current = Math.min(cap, progressRef.current + gap * 0.4);
					crawlDurationRef.current = Math.max(40_000, duration * 1.4);
					crawlStartedAtRef.current = Date.now();
					return;
				}
				setCrawling(false);
				return;
			}
			const t = Math.min(1, (Date.now() - started) / duration);
			// ease-out cubic — быстрый старт, мягкий хвост
			const eased = 1 - (1 - t) ** 3;
			const next = animFromRef.current + (target - animFromRef.current) * eased;
			const merged = Math.max(progressRef.current, next, serverFloorRef.current);
			if (merged > progressRef.current + 0.0005) {
				progressRef.current = merged;
				setProgress(merged);
			}
			if (t >= 1) {
				const cap = softCapRef.current;
				const gap = cap - progressRef.current;
				if (gap > 0.012 && serverFloorRef.current < 0.98) {
					animFromRef.current = progressRef.current;
					crawlTargetRef.current = Math.min(cap, progressRef.current + gap * 0.4);
					crawlDurationRef.current = Math.max(40_000, duration * 1.4);
					crawlStartedAtRef.current = Date.now();
					return;
				}
				setCrawling(false);
			}
		}, crawlIntervalMs);
		return () => window.clearInterval(id);
	}, [crawling, crawlIntervalMs]);

	return {
		progress,
		percent: Math.round(progress * 100),
		etaMsRemaining,
		applyTick,
		complete,
		reset,
	};
}

export interface FormatEtaMsLabels {
	/** Approx prefix from `common:generation.etaApprox` (e.g. "~", "ca."). */
	prefix: string;
	/** Resolved `common:generation.etaSeconds` template OR final string via ICU. */
	seconds: string;
	/** Resolved `common:generation.etaMinutes` template OR final string via ICU. */
	minutes: string;
}

/**
 * Format remaining ms. Prefer passing already-interpolated ICU strings from `t()`;
 * falls back to replacing `{prefix}` / `{count}` (and legacy `{{…}}`) in templates.
 */
/**
 * Soft-crawl от «пола» 0..1 (socket / stage / list map).
 * `floor01 == null` → idle (reset). Иначе всегда ползёт от max(seed, floor).
 * Для карточек списков и лоадеров без частых server ticks.
 */
export function useSoftProgressFloor(
	floor01: number | null | undefined,
	options: UseGenerationProgressOptions & { seed?: number } = {},
): UseGenerationProgressResult {
	const seed = options.seed ?? 0.05;
	const gen = useGenerationProgress(options);
	const applyTick = gen.applyTick;
	const reset = gen.reset;

	useEffect(() => {
		if (floor01 == null || Number.isNaN(floor01)) {
			reset();
			return;
		}
		applyTick({ progress: Math.max(seed, clamp01(floor01)) });
	}, [floor01, seed, applyTick, reset]);

	return gen;
}

export function formatEtaMs(
	ms: number | null | undefined,
	labels: FormatEtaMsLabels,
): string | null {
	if (ms == null || ms <= 0) return null;
	const sec = Math.ceil(ms / 1000);
	const apply = (template: string, count: number) =>
		template
			.replaceAll("{{prefix}}", labels.prefix)
			.replaceAll("{{count}}", String(count))
			.replaceAll("{prefix}", labels.prefix)
			.replaceAll("{count}", String(count));
	if (sec < 60) return apply(labels.seconds, sec);
	return apply(labels.minutes, Math.ceil(sec / 60));
}
